// Story 6.2: ModuleDashboard wrapper component
// Wraps DashboardCanvas for module context with ErrorBoundary and auto-fetch

import { useState, useEffect, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { DashboardCanvas } from '../../views/dashboard/components/DashboardCanvas';
import { dashboardService } from '../../views/dashboard/services/dashboard-api.service';
import { SaveStatusProvider } from '../../views/dashboard/contexts/SaveStatusContext';
import { DashboardSettingsProvider } from '../../views/dashboard/contexts/DashboardSettingsContext';
import { PresentModeProvider } from '../../views/dashboard/contexts/PresentModeContext';
import { useModuleToolbar } from './ModuleToolbarContext';

interface ModuleDashboardProps {
  moduleId: string;
}

function DashboardErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <div className="text-red-500 mb-4">Dashboard failed to load</div>
      <div className="text-sm text-muted-foreground mb-4">{error.message}</div>
      <button
        onClick={resetErrorBoundary}
        className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
      >
        Retry
      </button>
    </div>
  );
}

export function ModuleDashboard({ moduleId }: ModuleDashboardProps) {
  const [dashboardId, setDashboardId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { registerUndoRedo, unregisterUndoRedo } = useModuleToolbar();

  // TODO: Implement actual undo/redo for dashboard
  useEffect(() => {
    registerUndoRedo({
      onUndo: () => console.log('Dashboard Undo'),
      onRedo: () => console.log('Dashboard Redo'),
      canUndo: false,
      canRedo: false,
    });
    return () => unregisterUndoRedo();
  }, [registerUndoRedo, unregisterUndoRedo]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dashboard = await dashboardService.getOrCreateModuleDashboard(moduleId);
      setDashboardId(dashboard.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="text-red-500 mb-4">Error: {error}</div>
        <button
          onClick={loadDashboard}
          className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <PresentModeProvider>
      <DashboardSettingsProvider>
        <SaveStatusProvider>
          <ErrorBoundary FallbackComponent={DashboardErrorFallback} onReset={loadDashboard}>
            <DashboardCanvas dashboardId={dashboardId!} moduleId={moduleId} />
          </ErrorBoundary>
        </SaveStatusProvider>
      </DashboardSettingsProvider>
    </PresentModeProvider>
  );
}
